import { lookup } from '../content.ts';
import type { Event } from '../events.ts';
import type { Pos } from '../grid.ts';
import { type GeneratedEnemy, generateFloor } from '../mapgen.ts';
import { InvalidCommand, type RunState, type Unit, assertPhase, compareUnitIds, seedStreams, unitId } from '../state.ts';
import { unitStat } from '../stats.ts';
import type { CommandHandler, Ctx } from './types.ts';

/**
 * Starts the next floor with the server's per-floor seed: reseeds the four
 * streams, generates the floor (mapgen.ts chooses the standing party units'
 * tiles in the start room, then the enemies' and items' elsewhere) and places
 * the units. A down party unit gets no tile and no `unit_spawned`: it keeps
 * its position, and the grid ignores down units. Does not end a round.
 */
export type EnterFloorCommand = {
  readonly type: 'enter_floor';
  /** A 32-bit unsigned integer, `0 <= seed < 2^32`. */
  readonly seed: number;
};

const seedLimit = 0x100000000;

const spawnEnemy = (ctx: Ctx, e: GeneratedEnemy, id: string): Unit => ({
  id,
  side: 'enemy',
  kind: 'monster',
  monster: e.monster,
  level: e.level,
  exp: 0,
  passive: e.passive,
  skills: e.skills,
  equipment: null,
  boss: e.boss,
  pos: e.pos,
  facing: 's',
  hp: unitStat(ctx.content, { monster: e.monster, level: e.level, equipment: null, boss: e.boss }, 'hp'),
  ap: unitStat(ctx.content, { monster: e.monster, level: e.level, equipment: null, boss: e.boss }, 'ap'),
  sp: 0,
  stages: [],
  statuses: [],
  charging: null,
  firedOnce: [],
  tookDamageThisTurn: false,
  restTurns: 0,
  down: false,
});

/** Party units carry hp, ap, level, exp and skills over; the per-battle state resets. Down units stay down (and keep their `pos`). */
const placeParty = (u: Unit, pos: Pos): Unit => ({
  ...u,
  pos,
  facing: 'n',
  sp: 0,
  stages: [],
  statuses: [],
  charging: null,
  firedOnce: [],
  tookDamageThisTurn: false,
  restTurns: 0,
});

const spawned = (u: Unit): Event => ({
  type: 'unit_spawned',
  unit: u.id,
  monster: u.monster,
  kind: u.kind,
  side: u.side,
  level: u.level,
  pos: u.pos,
  boss: u.boss !== null,
});

export const enterFloor: CommandHandler<EnterFloorCommand> = (ctx, state, command) => {
  assertPhase(state, 'between_floors');
  const { seed } = command;
  if (typeof seed !== 'number' || !Number.isSafeInteger(seed) || seed < 0 || seed >= seedLimit) throw new InvalidCommand('bad_seed', 'seed must be an integer in [0, 2^32)');
  const dungeon = lookup(ctx.content.dungeons, state.dungeon);
  if (dungeon === null) throw new InvalidCommand('unknown_dungeon', `unknown dungeon ${state.dungeon}`, { dungeon: state.dungeon });
  const floorNumber = state.floorNumber + 1;
  const rng = seedStreams(seed);
  const members = state.party.map((id) => {
    const u = state.units.find((x) => x.id === id);
    if (!u) throw new InvalidCommand('party_unit_missing', `party unit ${id} is missing`, { unit: id });
    return u;
  });
  const standing = members.filter((u) => !u.down);
  const gen = generateFloor(ctx.content, dungeon, floorNumber, standing.length, rng.map);

  const party = members.map((u) => {
    if (u.down) return placeParty(u, u.pos);
    const pos = gen.partyTiles[standing.indexOf(u)];
    if (!pos) throw new InvalidCommand('no_room_for_party', 'no room for the party');
    return placeParty(u, pos);
  });
  const enemies = gen.enemies.map((e, i) => spawnEnemy(ctx, e, unitId(state.nextUnitId + i)));

  const next: RunState = {
    ...state,
    floorNumber,
    phase: 'playing',
    turn: 0,
    rng: { ...rng, map: gen.rng },
    nextUnitId: state.nextUnitId + enemies.length,
    units: [...party, ...enemies].sort((a, b) => compareUnitIds(a.id, b.id)),
    floor: gen.floor,
  };
  const events: Event[] = [{ type: 'floor_entered', floorNumber }, ...party.filter((u) => !u.down).map(spawned), ...enemies.map(spawned)];
  return { state: next, events };
};
